import { promiseWrapper, server, trackMpEvent } from '@/helpers';
import { PLACE_IMPRESSION, placeService } from '@/services';
import { getPopularAndSimilar } from '@/services/searchService';
import { Place } from '@/types/state/shared';
import * as Sentry from '@sentry/react';
import { useEffect, useState } from 'react';
import { EVENT_NAME, SCREEN_NAME, UNVERIFIED_SUBSCRIPTION_TYPE } from '../constants/tracking';
import { getCompanyTrackingProps } from '../helpers/tracking';

type OtherPlaces = {
  id: number;
  to: string;
  name: string;
  address: { street?: string; city?: string };
  rating: number;
};

export const UseProfilePage = (place: Place) => {
  const [popularPlaces, setPopularPlaces] = useState<OtherPlaces[]>([]);
  const [similarPlaces, setSimilarPlaces] = useState<OtherPlaces[]>([]);
  const [lastVisitedPlaces, setLastVisitedPlaces] = useState<OtherPlaces[]>([]);
  const isPremium = place.about?.settings?.subscriptionType === 'profilepagepremium';

  useEffect(() => {
    const getPopularAndSimilarPlaces = async () => {
      const { data, error } = await promiseWrapper(getPopularAndSimilar(place.id.toString()));

      if (data?.popular?.length > 0) {
        setPopularPlaces(
          data.popular.map((place) => {
            return {
              id: place.id,
              to: `/places/${place.about?.slug}-${place.id}`,
              name: place.name,
              address: { street: place.address.street, city: place.address.city },
              rating: place.rating.score,
            };
          }),
        );
      }

      if (data?.similar?.length > 0) {
        setSimilarPlaces(
          data.similar.map((place) => {
            return {
              id: place.id,
              to: `/places/${place.about?.slug}-${place.id}`,
              name: place.name,
              address: { street: place.address.street, city: place.address.city },
              rating: place.rating.score,
            };
          }),
        );
      }

      if (error) {
        Sentry.captureException(error);
      }
    };

    if (!isPremium) {
      getPopularAndSimilarPlaces();
    }

    if (!place.isVerifiedExternalPlace) {
      trackMpEvent(
        EVENT_NAME.COMPANY_VIEWED,
        {
          ...getCompanyTrackingProps({
            id: place.id,
            name: place.about?.name,
            screen: SCREEN_NAME.COMPANY_DETAILS,
            subType: UNVERIFIED_SUBSCRIPTION_TYPE,
          }),
        },
        false,
        true,
      );
    }

    const fetchRecentVisistedUserPlaces = async () => {
      const lastVisitedPlaces = JSON.parse(localStorage.getItem('mpLastVisitedPlaces') || '[]');
      const { data, error } = await promiseWrapper(
        server.request.get('/places/recent', {
          params: { ids: lastVisitedPlaces.join(',') },
        }),
      );

      if (error) {
        Sentry.captureException(error);
      }

      if (data?.data?.length > 0) {
        setLastVisitedPlaces(
          data.data.map((place) => ({
            id: place.id,
            to: `/places/${place.about?.slug}-${place.id}`,
            name: place.about?.name,
            rating: place.rating?.score,
            address: { street: place.contact?.address?.street, city: place.contact?.address?.city },
          })),
        );
      }
    };

    fetchRecentVisistedUserPlaces();

    placeService.storePlaceImpressions([place.id], PLACE_IMPRESSION.PLACE_IMPRESSION);
  }, []);

  return { popularPlaces, similarPlaces, lastVisitedPlaces };
};
